import axios from 'axios';
import { API_URL } from '../config';
import { getAuthHeaders } from '../utils/auth';

const aiOrchestratorService = {
  // Get all orchestrators
  getAllOrchestrators: async (params = {}) => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-orchestrators`, {
        headers: getAuthHeaders(),
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          sortField: params.sortField || 'createdAt',
          sortOrder: params.sortOrder || 'desc',
          status: params.status || '',
          search: params.search || ''
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching orchestrators:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch orchestrators');
    }
  },

  // Get orchestrator by ID
  getOrchestratorById: async (id) => {
    try {
      console.log('id', id);
      const response = await axios.get(`${API_URL}/api/ai-orchestrators/${id}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error('Error fetching orchestrator:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch orchestrator');
    }
  },

  // Create new orchestrator
  createOrchestrator: async (data) => {
    try {
      const response = await axios.post(`${API_URL}/api/ai-orchestrators`, data, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error('Error creating orchestrator:', error);
      throw new Error(error.response?.data?.error || 'Failed to create orchestrator');
    }
  },

  // Update orchestrator
  updateOrchestrator: async (id, data) => {
    try {
      const response = await axios.patch(`${API_URL}/api/ai-orchestrators/${id}`, data, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error('Error updating orchestrator:', error);
      throw new Error(error.response?.data?.error || 'Failed to update orchestrator');
    }
  },

  // Delete orchestrator
  deleteOrchestrator: async (id) => {
    try {
      const response = await axios.delete(`${API_URL}/api/ai-orchestrators/${id}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error('Error deleting orchestrator:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete orchestrator');
    }
  },

  // Add or update an agent
  upsertAgent: async (orchestratorId, agentData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-orchestrators/${orchestratorId}/agents`,
        agentData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error upserting agent:', error);
      throw new Error(error.response?.data?.error || 'Failed to upsert agent');
    }
  },

  // Delete an agent
  deleteAgent: async (orchestratorId, agentId) => {
    try {
      const response = await axios.delete(
        `${API_URL}/api/ai-orchestrators/${orchestratorId}/agents/${agentId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting agent:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete agent');
    }
  },

  // Get tasks
  getTasks: async (params) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ai-orchestrators/tasks`,
        {
          headers: getAuthHeaders(),
          params
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch tasks');
    }
  },

  // Update task
  updateTask: async (orchestratorId, taskId, data) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/ai-orchestrators/${orchestratorId}/tasks/${taskId}`,
        data,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw new Error(error.response?.data?.error || 'Failed to update task');
    }
  },

  // Delete task
  deleteTask: async (orchestratorId, taskId) => {
    try {
      const response = await axios.delete(
        `${API_URL}/api/ai-orchestrators/${orchestratorId}/tasks/${taskId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting task:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete task');
    }
  },

  // Execute a task
  executeTask: async (taskData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-orchestrators/tasks/execute`,
        taskData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error executing task:', error);
      throw new Error(error.response?.data?.error || 'Failed to execute task');
    }
  },

  // Schedule a task
  scheduleTask: async (taskData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-orchestrators/tasks/schedule`,
        taskData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error scheduling task:', error);
      throw new Error(error.response?.data?.error || 'Failed to schedule task');
    }
  },

  // Get orchestrator statistics
  getStatistics: async (orchestratorId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ai-orchestrators/${orchestratorId}/statistics`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch statistics');
    }
  },

  // Get orchestrator stats
  getOrchestratorStats: async () => {
    try {
      console.log('getOrchestratorStats');
      const response = await axios.get(
        `${API_URL}/api/ai-orchestrators/stats`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching orchestrator stats:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch orchestrator stats');
    }
  },

  // Get agent memory
  getAgentMemory: async (params) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ai-orchestrators/memory`,
        {
          headers: getAuthHeaders(),
          params
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching agent memory:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch agent memory');
    }
  },

  // Create memory
  createMemory: async (orchestratorId, data) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-orchestrators/${orchestratorId}/memory`,
        data,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating memory:', error);
      throw new Error(error.response?.data?.error || 'Failed to create memory');
    }
  },

  // Update memory
  updateMemory: async (orchestratorId, memoryId, data) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/ai-orchestrators/${orchestratorId}/memory/${memoryId}`,
        data,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating memory:', error);
      throw new Error(error.response?.data?.error || 'Failed to update memory');
    }
  },

  // Delete memory
  deleteMemory: async (orchestratorId, memoryId) => {
    try {
      const response = await axios.delete(
        `${API_URL}/api/ai-orchestrators/${orchestratorId}/memory/${memoryId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting memory:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete memory');
    }
  }
};

export default aiOrchestratorService; 